if (!customElements.get('main-menu')) {
  class MainMenu extends HTMLElement {
    constructor() {
      super();
      this.hamburger = this.querySelector('[data-hamburger]');
      this.navigationHeight = this.querySelector('[data-nav]').offsetHeight;
      this.dropdown = this.querySelector('[data-dropdown]');
      this.account = this.querySelector('[data-account]');
      this.accountModal = this.querySelector('[data-account-modal]');
      this.accountInner = this.querySelector('[data-account-inner]');
      this.accountMobile = this.querySelector('[data-account-mobile]');
      this.langSwitches = this.querySelectorAll('[data-lang-switch]');
      this.closeBtn = this.querySelector('[data-close]');
      this.service = this.querySelector('[data-service]');
    }

    connectedCallback() {
      this.initEventListeners();
      this.initLangSwitches();
    }

    initEventListeners() {
      window.addEventListener('scroll', () => this.handleScroll());
      this.hamburger.addEventListener('click', () => this.handleHamburgerClick());
      this.closeBtn?.addEventListener('click', (e) => { e.stopPropagation(); this.toggleMenu(); });
      this.dropdown.addEventListener('click', (e) => {
        if (!this.querySelector('[data-menu-content]')?.contains(e.target)) {
          this.toggleMenu();
        }
      });
      this.account.addEventListener('click', () => this.toggleAccount());
      this.service?.addEventListener('click', () => this.toggleService());
      this.accountModal?.addEventListener('click', (e) => {
        if (!this.accountInner.contains(e.target)) {
          e.stopPropagation();
          this.closeAccount();
        }
      });
      this.accountMobile.addEventListener('click', () => this.toggleMobileAccount());
      this.langSwitches.forEach((link) => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          this.setLanguage(link.dataset.lang);
        });
      });
    }

    initLangSwitches() {
      const currentLocale = Shopify.locale;
      this.langSwitches.forEach((link) => {
        if (link.dataset.lang === currentLocale) {
          link.setAttribute('data-active', '');
        }
      });
    }

    setLanguage(languageCode) {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/localization';
      form.style.display = 'none';

      const fields = {
        locale_code: languageCode,
        form_type: 'localization',
        _method: 'put',
        utf8: '✓',
      };

      for (const name in fields) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = fields[name];
        form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();
    }

    // Handle hamburger click
    handleHamburgerClick() {
      if (this.account.hasAttribute('open')) {
        this.account.removeAttribute('open');
        this.removeAttribute('data-open');
        this.removeAttribute('data-form');
        this.hamburger.removeAttribute('data-open');
        this.enableBodyScroll();
        return;
      }

      const wasMenuOpen = this.hasAttribute('data-open');

      // Service overlay should not stay open when menu is toggled.
      if (this.service?.hasAttribute('open')) {
        this.service.removeAttribute('open');
        // When menu is opening, `data-form` should be removed.
        if (!wasMenuOpen) {
          this.removeAttribute('data-form');
        }
      }

      // When menu is opening, remove `data-form`.
      if (!wasMenuOpen) {
        this.removeAttribute('data-form');
      }

      this.toggleMenu();

      // If menu is closing and there are no other overlays open, remove styles.
      if (wasMenuOpen && !this.account?.hasAttribute('open') && !this.service?.hasAttribute('open')) {
        this.removeAttribute('data-form');
      }
    }

    // Toggle main menu
    toggleMenu() {
      this.toggleAttribute('data-open');
      this.hamburger.toggleAttribute('data-open');
      this.toggleBodyHidden();
    }

    // Toggle account
    toggleAccount() {
      // `details[open]` state changes after the click event, so we read it on next tick.
      setTimeout(() => {
        const isAccountOpen = this.account?.hasAttribute('open');

        if (isAccountOpen) {
          // Account should replace service; close service only.
          this.service?.removeAttribute('open');

          // Keep `data-form` while any overlay is open.
          this.setAttribute('data-form', '');

          // Account overlay needs body scroll lock.
          if (!document.body.hasAttribute('data-hidden')) {
            this.toggleBodyHidden();
          }
          return;
        }

        // Account closed: remove overlay styles if service is not open.
        if (!this.service?.hasAttribute('open')) {
          this.removeAttribute('data-form');
        }
        this.enableBodyScroll();
      }, 0);
    }

    // Toggle service (consultation).
    toggleService() {
      // `details[open]` state changes after the click event, so we read it on next tick.
      setTimeout(() => {
        const isServiceOpen = this.service?.hasAttribute('open');

        if (isServiceOpen) {
          // Service replaces account; close account only.
          if (this.account?.hasAttribute('open')) {
            this.closeAccount();
          }

          // Keep `data-form` while service is open.
          this.setAttribute('data-form', '');
          return;
        }

        // Service closed: remove overlay styles if account is not open.
        if (!this.account?.hasAttribute('open')) {
          this.removeAttribute('data-form');
          // Service overlay is full height; no body scroll lock needed.
          this.enableBodyScroll();
        }
      }, 0);
    }

    // Close account dropdown
    closeAccount() {
      if (this.account.hasAttribute('open')) {
        this.account.removeAttribute('open');
        // Service (consultation) may still be open, so only remove `data-form`
        // when service isn't open.
        if (this.hasAttribute('data-form') && !this.service?.hasAttribute('open')) {
          this.removeAttribute('data-form');
        }
        // Service overlay is full height; no body scroll lock needed.
        this.enableBodyScroll();
      }
    }

    // Toggle mobile account
    toggleMobileAccount() {
      this.account.toggleAttribute('open');
      this.toggleAttribute('data-open');
      this.toggleAttribute('data-form');
    }

    // Handle scroll-based styling
    handleScroll() {
      this.toggleAttribute('data-scroll', window.scrollY > this.navigationHeight * 0.6);
    }

    // Toggle body scroll lock with scrollbar-width compensation to prevent layout jump
    toggleBodyHidden() {
      setTimeout(() => {
        if (!this.hasAttribute('data-open') || !document.body.hasAttribute('data-hidden')) {
          const isOpening = !document.body.hasAttribute('data-hidden');
          if (isOpening) {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.paddingRight = scrollbarWidth + 'px';
            document.documentElement.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');
          } else {
            document.body.style.paddingRight = '';
            document.documentElement.style.removeProperty('--scrollbar-width');
          }
          document.body.toggleAttribute('data-hidden');
        }
      }, 0);
    }

    // Enable body scroll when closing menu
    enableBodyScroll() {
      document.body.removeAttribute('data-hidden');
      document.body.style.paddingRight = '';
      document.documentElement.style.removeProperty('--scrollbar-width');
    }
  }

  customElements.define('main-menu', MainMenu);
}